import { useState, useRef, useEffect, type ReactNode } from 'react'
import { User, LogOut, Bell, CreditCard, LifeBuoy, LayoutGrid } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { HeaderUserSwitcher, MockUser } from '@airclou/ui'
import { signOutOfSuite } from '@airclou/auth-react'
import { getServiceApiUrl, getServiceUrl } from '@airclou/utils'
import { BrandingConfig } from './Sidebar'
import { NavbarSearch, AdvancedSearchConfig } from './components/NavbarSearch'
import { detectAppFromPathname, getCurrentApp } from './utils/appDetection'
import { isProductionEnvironment } from './utils/productionCheck'
import { useNotificationContext } from './notifications/NotificationProvider'
import type { Notification } from './notifications/types'

// Profile menu item interface for configurable dropdown items
export interface ProfileMenuItem {
  id: string
  label: string
  icon: React.ComponentType<{ className?: string }>
  onClick: () => void
  // Optional condition to show the item (defaults to true)
  show?: boolean
}

// Helper function to get the API base URL with proper environment detection
const getApiBaseUrl = (): string => {
  // Check for explicit environment variable first
  if (typeof window !== 'undefined' && window.location) {
    // Browser environment - use window.location for environment detection
    const hostname = window.location.hostname
    const isLocalhost = hostname === 'localhost' || hostname === '127.0.0.1'
    
    if (isLocalhost) {
      // In localhost, check the port to determine which app
      const port = window.location.port
      if (port === '5174') {
        // Dashboard frontend port
        return 'http://localhost:3003/api'
      } else if (port === '5173') {
        // Playbook frontend port
        return 'http://localhost:3002/api'
      } else if (port === '5175') {
        // Helpdesk frontend port
        return 'http://localhost:4010/api'
      }
      // Default to playbook
      return 'http://localhost:3002/api'
    } else {
      // In production, use app.airclou.com with service paths
      if (hostname === 'app.airclou.com') {
        // Determine which app based on the path (handles workspace-prefixed URLs)
        const pathname = window.location.pathname
        const app = detectAppFromPathname(pathname)
        if (app) {
          return `https://app.airclou.com/${app}/api`
        }
        if (pathname === '/') {
          return 'https://app.airclou.com/playbook/api'
        }
        // Default to playbook
        return 'https://app.airclou.com/playbook/api'
      }
      // Legacy domain support for backward compatibility
      else if (hostname === 'dashboard.airclou.com') {
        return 'https://dashboard.airclou.com/api'
      } else if (hostname === 'playbook.airclou.com') {
        return 'https://playbook.airclou.com/api'
      }
      // Default to new domain structure
      return 'https://app.airclou.com/playbook/api'
    }
  }
  
  // Fallback for build time or SSR environments
  try {
    // Try to access Vite environment variables if available
    const viteEnv = (import.meta as any).env
    if (viteEnv?.VITE_API_URL) {
      return viteEnv.VITE_API_URL
    }
    
    // Check for development mode indicators (avoid using process.env in browser)
    const isDevelopment = viteEnv?.MODE === 'development' || viteEnv?.DEV
    
    return isDevelopment 
      ? 'http://localhost:3002/api' 
      : 'https://playbook.airclou.com/api'
  } catch {
    // Ultimate fallback - assume production if no environment info available
    return 'https://playbook.airclou.com/api'
  }
}

// Checks app-specific token keys first, then falls back to generic keys.
const getAuthToken = (): string | null => {
  try {
    const app = typeof window !== 'undefined' ? getCurrentApp() : null

    if (app) {
      const t = localStorage.getItem(`${app}:authToken`)
      if (t) return t
    }

    return localStorage.getItem('dashboard:authToken') ||
           localStorage.getItem('playbook:authToken') ||
           localStorage.getItem('helpdesk:authToken') ||
           null
  } catch {
    return null
  }
}

// Helper function to get current user ID for demo token mapping
const getCurrentUserId = (): string => {
  try {
    return localStorage.getItem('playbook:currentUserId') || 'usr_michael_chen_001'
  } catch {
    return 'usr_michael_chen_001'
  }
}

// Demo token mapping - simplified version for shell package
const getUserToken = (userId: string): string => {
  const tokenMapping: Record<string, string> = {
    'usr_michael_chen_001': 'demo-token-michael-chen',
    'usr_sarah_wilson_001': 'demo-token-sarah-wilson',
    'usr_james_wilson_001': 'demo-token-james-wilson',
    'usr_emily_davis_001': 'demo-token-emily-davis',
    'usr_lisa_thompson_001': 'demo-token-lisa-thompson',
    'usr_david_kim_001': 'demo-token-david-kim',
    'usr_alex_rodriguez_001': 'demo-token-alex-rodriguez',
    'usr_gangcailin_001': 'demo-token-gangcai-lin',
  }
  return tokenMapping[userId] || 'demo-token-michael-chen'
}

// Helper function to get authentication token with production awareness
const getProductionAwareToken = (): string | null => {
  let token = getAuthToken()
  if (!token && !isProductionEnvironment()) {
    // Only fall back to demo tokens in development
    const currentUserId = getCurrentUserId()
    token = getUserToken(currentUserId)
  }
  return token
}

// Added helper: if name looks like an email, show local part; otherwise show name; fallback to email local part or 'User'
const formatDisplayName = (user?: MockUser | null): string => {
  if (!user) return 'User'
  const name = user.name?.trim()
  if (name) {
    return name.includes('@') ? name.split('@')[0] : name
  }
  const email = user.email?.trim()
  if (email) return email.split('@')[0]
  return 'User'
}

export interface NavbarProps {
  onSearch?: (query: string) => void
  placeholder?: string
  /** Path prefixes that should sync the search query from URL. Defaults to ['/dashboard/search'] */
  syncUrlPaths?: string[]
  /** Configuration for advanced search dropdown */
  advancedSearch?: AdvancedSearchConfig
  // User Switcher props
  users?: MockUser[]
  currentUserId?: string
  onUserChange?: (userId: string) => void
  playbookOwner?: string
  hideUserSwitcher?: boolean
  // Branding configuration (required to ensure proper app identification for sign-out)
  branding: BrandingConfig
  /** Items above Sign out. Omitted → the default items; `[]` → none. */
  profileMenuItems?: ProfileMenuItem[]
  /** Custom sign-out handler. If provided, this will be called instead of the default sign-out logic. */
  onSignOut?: () => void
  /**
   * Opt-in app-specific content rendered on the right side of the navbar,
   * before the notification bell. Used by apps that need an ambient indicator
   * (e.g. Helpdesk's "Team online" pill). Omit to keep the navbar unchanged.
   */
  rightSlot?: ReactNode
  /**
   * Opt-in replacement for the default inline {@link NavbarSearch}. When
   * provided, this node is rendered in the search slot instead of the default
   * search input. Used by apps that want a compact search trigger + command
   * palette (e.g. Helpdesk's Ctrl+K search) rather than an always-visible
   * input. Omit to keep the default inline search.
   */
  searchSlot?: ReactNode
  /**
   * Opt-in insertion between the search slot and the right-side controls.
   * Used by apps that need an in-navbar workspace indicator or tab bar
   * (e.g. Helpdesk's open-ticket tabs). Omit to keep the navbar unchanged.
   */
  centerSlot?: ReactNode
}

export function Navbar({ 
  onSearch,
  placeholder,
  syncUrlPaths,
  advancedSearch,
  // User Switcher props
  users,
  currentUserId,
  onUserChange,
  playbookOwner,
  hideUserSwitcher = false,
  // Branding configuration
  branding,
  // Profile menu items
  profileMenuItems,
  // Custom sign-out handler
  onSignOut,
  rightSlot,
  searchSlot,
  centerSlot
}: NavbarProps) {
  const { t } = useTranslation('translation')
  const navigate = useNavigate()
  const [isProfileDropdownOpen, setIsProfileDropdownOpen] = useState(false)
  
  // Get notification adapter from provider (may be null if no provider wraps the app)
  const notificationCtx = useNotificationContext()
  const notificationAdapter = notificationCtx?.adapter
  
  // Extract workspace and app from current URL
  const getWorkspaceAndApp = (): { workspaceSlug: string; appName: string } => {
    const pathname = window.location.pathname
    // Pattern: /:workspace/:app/...
    const match = pathname.match(/^\/([^/]+)\/([^/]+)/)
    if (match) {
      return { workspaceSlug: match[1], appName: match[2] }
    }
    // Fallback to default workspace and playbook app
    return { workspaceSlug: 'default', appName: 'playbook' }
  }
  const [isNotificationDropdownOpen, setIsNotificationDropdownOpen] = useState(false)
  const [notifications, setNotifications] = useState<Notification[]>([])
  const [isLoadingNotifications, setIsLoadingNotifications] = useState(false)
  const [notificationError, setNotificationError] = useState<string | null>(null)
  
  // Get current user information
  const currentUser = users?.find(user => user.id === currentUserId)
  const isBillingAdmin = currentUser?.role === 'billing_admin'

  const profileRef = useRef<HTMLDivElement>(null)
  const notificationRef = useRef<HTMLDivElement>(null)

  // Load notifications from API (uses adapter from NotificationProvider)
  useEffect(() => {
    const loadNotifications = async () => {
      if (!notificationAdapter) {
        setNotifications([])
        setIsLoadingNotifications(false)
        return
      }
      try {
        setIsLoadingNotifications(true)
        setNotificationError(null)
        
        const response = await notificationAdapter.getNotifications({ limit: 10, sort: 'created_at', order: 'desc' })
        setNotifications(response.data as Notification[])
      } catch (error) {
        console.error('Failed to load notifications:', error)
        setNotificationError('Failed to load notifications')
        setNotifications([])
      } finally {
        setIsLoadingNotifications(false)
      }
    }
    
    loadNotifications()
  }, [notificationAdapter])

  // Click outside to close
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (profileRef.current && !profileRef.current.contains(event.target as Node)) {
        setIsProfileDropdownOpen(false)
      }
      if (notificationRef.current && !notificationRef.current.contains(event.target as Node)) {
        setIsNotificationDropdownOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const handleProfileClick = () => {
    setIsProfileDropdownOpen(!isProfileDropdownOpen)
  }

  const handleAccountSettingsClick = () => {
    const { workspaceSlug, appName } = getWorkspaceAndApp()
    setIsProfileDropdownOpen(false)
    navigate(`/${workspaceSlug}/${appName}/settings/account`)
  }

  const handleBillingClick = () => {
    const { workspaceSlug, appName } = getWorkspaceAndApp()
    setIsProfileDropdownOpen(false)
    navigate(`/${workspaceSlug}/${appName}/billing`)
  }

  const handleHelpSupportClick = () => {
    const { workspaceSlug, appName } = getWorkspaceAndApp()
    setIsProfileDropdownOpen(false)
    navigate(`/${workspaceSlug}/${appName}/settings/help-support`)
  }

  const handleSignOutClick = async () => {
    setIsProfileDropdownOpen(false)
    
    // If a custom sign-out handler is provided, use it instead of the default logic
    if (onSignOut) {
      onSignOut()
      return
    }
    
    try {
      // Get authentication token with production awareness
      const token = getProductionAwareToken()
      
      // Call logout endpoint on Auth service if we have a valid token
      if (token && !token.startsWith('demo-token-')) {
        const authApiUrl = getServiceApiUrl('auth')
        await fetch(`${authApiUrl}/auth/logout`, {
          method: 'POST',
          credentials: 'include',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
        })
      }
      
    } catch (error) {
      console.error('Sign out error:', error)
      // Cleanup and navigation will happen in finally block
    } finally {
      // Notify listeners (e.g. presence heartbeat) *before* clearing the token
      // so they can send authenticated teardown signals (keepalive fetch).
      window.dispatchEvent(new CustomEvent('session:logout-start', { detail: { reason: 'manual' } }));

      // Always clear authentication data and navigate, regardless of success/failure
      // Determine the current app namespace from branding
      const appName = branding.name.toLowerCase()
      const appNamespace = `${appName}:`
      
      // Preserve the UI working-set across a manual logout: open ticket tabs
      // and unsent drafts are restored on re-login (same browser). A manual
      // sign-out should not destroy in-progress work — only inactivity logout
      // wipes everything. Suffixes are namespace-relative (e.g. for helpdesk:
      // these match helpdesk:openTabs:* and helpdesk:draft:*).
      const preservedSuffixes = ['openTabs:', 'draft:']

      // Clear this app's own keys first (everything but the preserved
      // working-set above), then hand off to the suite sweep below.
      const keys = Object.keys(localStorage)
      for (const key of keys) {
        // Only clear keys that belong to this app (have the app namespace prefix)
        // OR are demo tokens for this app
        if (key.startsWith(appNamespace)) {
          const suffix = key.slice(appNamespace.length)
          if (preservedSuffixes.some((p) => suffix.startsWith(p))) {
            continue
          }
          localStorage.removeItem(key)
        } else {
          // For non-namespaced keys, only clear demo tokens
          const value = localStorage.getItem(key)
          if (value && value.startsWith('demo-token-')) {
            localStorage.removeItem(key)
          }
        }
      }

      // The cookie just invalidated is a single slot for `.airclou.com`, so it
      // backed every app on this origin — not only this one. Sweeping just
      // `${appName}:` leaves siblings (helpdesk, playbook, conductor, forms)
      // holding a credential with no session behind it, and leaves their open
      // tabs never told (ID-7). An earlier comment here justified the narrow
      // sweep as avoiding "cross-app logout issues"; sharing a domain is
      // precisely what makes the wide sweep necessary.
      //
      // `clearSuiteAuthKeys` is an allowlist of auth key suffixes, so siblings'
      // preferences and in-progress work survive exactly as this app's do.
      // Safe to broadcast from here: a BroadcastChannel never delivers to the
      // object that sent it, so this tab does not hear its own message.
      signOutOfSuite()

      // Navigate to environment-appropriate auth login page
      const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1'
      const authLoginUrl = isLocalhost ? `http://localhost:4000/login?app=${appName}` : `https://auth.airclou.com/login?app=${appName}`
      window.location.href = authLoginUrl
    }
  }

  const handleNotificationClick = () => {
    setIsProfileDropdownOpen(false)
    setIsNotificationDropdownOpen(!isNotificationDropdownOpen)
  }

  const handleMarkAsRead = async (notificationId: string) => {
    if (!notificationAdapter) return
    try {
      await notificationAdapter.markAsRead(notificationId)
      setNotifications(prev => 
        prev.map(notification => 
          notification.id === notificationId 
            ? { ...notification, isRead: true, read_at: new Date().toISOString() }
            : notification
        )
      )
    } catch (error) {
      console.error('Failed to mark notification as read:', error)
    }
  }

  const handleMarkAllAsRead = async () => {
    if (!notificationAdapter) return
    try {
      await notificationAdapter.markAllAsRead()
      setNotifications(prev => 
        prev.map(notification => ({ 
          ...notification, 
          isRead: true, 
          read_at: new Date().toISOString() 
        }))
      )
    } catch (error) {
      console.error('Failed to mark all notifications as read:', error)
    }
  }

  const formatNotificationTime = (timestamp?: string) => {
    if (!timestamp) return 'N/A'
    
    const date = new Date(timestamp)
    const now = new Date()
    const diff = now.getTime() - date.getTime()
    const minutes = Math.floor(diff / (1000 * 60))
    const hours = Math.floor(diff / (1000 * 60 * 60))
    const days = Math.floor(diff / (1000 * 60 * 60 * 24))

    if (days > 0) {
      return `${days}d ago`
    } else if (hours > 0) {
      return `${hours}h ago`
    } else if (minutes > 0) {
      return `${minutes}m ago`
    } else {
      return 'Just now'
    }
  }

  const unreadCount = notifications.filter(n => !n.isRead && !n.read_at).length

  return (
    <nav className="bg-white dark:bg-gray-950 border-b border-gray-200 dark:border-gray-800 px-6 py-2">
      <div className={`flex items-center justify-between${centerSlot ? ' gap-4' : ''}`}>
        {/* Left side - Search (apps may replace the default inline input via searchSlot) */}
        {searchSlot ?? (
          <NavbarSearch
            onSearch={onSearch}
            placeholder={placeholder}
            getAuthToken={getProductionAwareToken}
            apiBaseUrl={getApiBaseUrl()}
            syncUrlPaths={syncUrlPaths}
            advancedSearch={advancedSearch}
          />
        )}

        {/* Center slot - app-specific content (e.g. Helpdesk ticket tabs) */}
        {centerSlot && <div className="flex-1 min-w-0 flex items-end">{centerSlot}</div>}
        
        {/* Right side - Dark Mode Toggle and Profile dropdown */}
        <div className="flex items-center space-x-4">
          {/* App-specific right slot (opt-in; e.g. Helpdesk team-online pill) */}
          {rightSlot}

          {/* User Switcher */}
          {users && currentUserId && onUserChange && !hideUserSwitcher && (
            <HeaderUserSwitcher
              users={users}
              currentUserId={currentUserId}
              onUserChange={onUserChange}
              playbookOwner={playbookOwner}
            />
          )}

          {/* All apps — the launcher, at the same level as the bell rather than
              inside the profile menu: leaving this app for another one is
              navigation, not an account action. Built into the shell so every
              app carries it whether or not it passes its own profile items.
              An anchor, not a button: plain navigation that should survive
              middle-click and open-in-new-tab. */}
          <a
            href={getServiceUrl('launcher')}
            title={t('navbar.allApps')}
            aria-label={t('navbar.allApps')}
            data-testid="navbar-launcher"
            className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
          >
            <LayoutGrid className="w-5 h-5 text-gray-600 dark:text-gray-300" />
          </a>

          {/* Notifications — only where an app mounted a NotificationProvider.
              Without an adapter there is nothing to show, and the "View all"
              link below points at a notifications route only playbook and
              helpdesk register; everywhere else it landed on a dead URL
              (dashboard's 404 page, a blank pane in admin and conductor, a
              silent redirect in form-builder). */}
          {notificationAdapter && (
            <div className="relative" ref={notificationRef}>
              <button
                onClick={handleNotificationClick}
                className="relative p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
              >
                <Bell className="w-5 h-5 text-gray-600 dark:text-gray-300" />
                {unreadCount > 0 && (
                  <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs font-medium rounded-full flex items-center justify-center">
                    {unreadCount > 9 ? '9+' : unreadCount}
                  </span>
                )}
              </button>

                           {/* Notifications Dropdown */}
               {isNotificationDropdownOpen && (
                 <div className="absolute right-0 mt-2.5 w-[32rem] bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-lg shadow-lg z-[10000] max-h-[32rem] overflow-hidden">
                  <div className="px-4 py-3 border-b border-gray-100 dark:border-gray-800">
                    <div className="flex items-center justify-between">
                      <h3 className="text-sm font-medium text-foreground">
                        {t('navbar.notifications.title')}
                      </h3>
                      {unreadCount > 0 && (
                        <button
                          onClick={handleMarkAllAsRead}
                          className="text-xs text-primary hover:text-[hsl(var(--primary-hover))] font-medium"
                        >
                          {t('navbar.notifications.markAllAsRead')}
                        </button>
                      )}
                    </div>
                    {unreadCount > 0 && (
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        {unreadCount} {unreadCount > 1 ? t('navbar.notifications.unreadCountPlural') : t('navbar.notifications.unreadCount')}
                      </p>
                    )}
                  </div>
                                 
                   <div className="max-h-96 overflow-y-auto">
                    {isLoadingNotifications ? (
                      <div className="px-4 py-8 text-center">
                        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary mx-auto mb-3"></div>
                        <p className="text-sm text-gray-500 dark:text-gray-400">{t('navbar.notifications.loadingNotifications')}</p>
                      </div>
                    ) : notificationError ? (
                      <div className="px-4 py-8 text-center">
                        <Bell className="h-8 w-8 text-red-400 mx-auto mb-3" />
                        <p className="text-sm text-red-500 dark:text-red-400">{notificationError}</p>
                      </div>
                    ) : notifications.length > 0 ? (
                      <div className="py-2">
                        {notifications.map((notification) => {
                          const isRead = notification.isRead || !!notification.read_at
                          const timestamp = notification.timestamp || notification.created_at
                        
                          return (
                            <div
                              key={notification.id}
                              className={`px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors cursor-pointer border-l-4 ${
                                isRead 
                                  ? 'border-transparent' 
                                  : 'border-primary bg-primary/5 dark:bg-primary/10'
                              }`}
                              onClick={() => handleMarkAsRead(notification.id)}
                            >
                              <div className="flex gap-3">
                                <div className="flex-1 min-w-0">
                                  <div className="flex items-start justify-between gap-3">
                                    <div className="min-w-0 flex-1">
                                      <p className="text-sm text-gray-600 dark:text-gray-400 mt-1 leading-relaxed break-words">
                                        {notification.message}
                                      </p>
                                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                                        {formatNotificationTime(timestamp)}
                                      </p>
                                    </div>
                                  
                                    {!isRead && (
                                      <div className="flex-shrink-0 w-2 h-2 bg-primary rounded-full mt-3"></div>
                                    )}
                                  </div>
                                </div>
                              </div>
                            </div>
                          )
                        })}
                      </div>
                    ) : (
                      <div className="px-4 py-8 text-center">
                        <Bell className="h-8 w-8 text-gray-400 mx-auto mb-3" />
                        <h4 className="text-sm font-medium text-foreground mb-2">
                          {t('navbar.notifications.noNotifications')}
                        </h4>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          {t('navbar.notifications.allCaughtUp')}
                        </p>
                      </div>
                    )}
                  </div>
                
                  <div className="border-t border-gray-100 dark:border-gray-800 px-4 py-3">
                    <button
                      onClick={() => {
                        const { workspaceSlug, appName } = getWorkspaceAndApp()
                        setIsNotificationDropdownOpen(false)
                        navigate(`/${workspaceSlug}/${appName}/notifications/unread`)
                      }}
                      className="w-full text-right text-xs text-primary hover:text-[hsl(var(--primary-hover))] font-medium"
                    >
                      {t('navbar.notifications.viewAll')}
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Profile Dropdown */}
          <div className="relative" ref={profileRef}>
            <button
              onClick={handleProfileClick}
              data-tour-id="header-profile-avatar"
              className="p-1 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
            >
              <div className="w-8 h-8 bg-primary rounded-full flex items-center justify-center">
                <User className="w-4 h-4 text-primary-foreground" />
              </div>
            </button>

            {/* Profile Dropdown Menu */}
            {isProfileDropdownOpen && (
              <div className="absolute right-0 mt-2 w-64 bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-lg shadow-lg z-[10000]">
                <div className="px-4 py-3 border-b border-gray-100 dark:border-gray-800">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-primary rounded-full flex items-center justify-center flex-shrink-0">
                      <User className="w-5 h-5 text-primary-foreground" />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div
                        className="text-sm font-medium text-foreground truncate"
                        title={currentUser?.name || currentUser?.email || 'User'}
                      >
                        {formatDisplayName(currentUser)}
                      </div>
                      {currentUser?.email && (
                        <div
                          className="text-xs text-gray-500 dark:text-gray-400 truncate"
                          title={currentUser.email}
                        >
                          {currentUser.email}
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                
                {profileMenuItems?.length !== 0 && (
                <div className="py-2">
                  {profileMenuItems ? (
                    // Render custom menu items if provided
                    profileMenuItems
                      .filter(item => item.show !== false)
                      .map((item) => (
                        <button
                          key={item.id}
                          onClick={() => {
                            setIsProfileDropdownOpen(false)
                            item.onClick()
                          }}
                          className="w-full px-4 py-2 text-left hover:bg-gray-50 dark:hover:bg-gray-800 flex items-center space-x-3 transition-colors"
                        >
                          <item.icon className="w-4 h-4 text-gray-400 dark:text-gray-500" />
                          <span className="text-sm text-gray-700 dark:text-gray-300">{item.label}</span>
                        </button>
                      ))
                  ) : (
                    // Default menu items for backward compatibility
                    <>
                      <button 
                        onClick={handleAccountSettingsClick}
                        className="w-full px-4 py-2 text-left hover:bg-gray-50 dark:hover:bg-gray-800 flex items-center space-x-3 transition-colors"
                      >
                        <User className="w-4 h-4 text-gray-400 dark:text-gray-500" />
                        <span className="text-sm text-gray-700 dark:text-gray-300">{t('navbar.profile.accountSettings')}</span>
                      </button>
                      {isBillingAdmin && (
                        <button 
                          onClick={handleBillingClick}
                          className="w-full px-4 py-2 text-left hover:bg-gray-50 dark:hover:bg-gray-800 flex items-center space-x-3 transition-colors"
                        >
                          <CreditCard className="w-4 h-4 text-gray-400 dark:text-gray-500" />
                          <span className="text-sm text-gray-700 dark:text-gray-300">{t('navbar.profile.billing')}</span>
                        </button>
                      )}
                      <button 
                        onClick={handleHelpSupportClick}
                        className="w-full px-4 py-2 text-left hover:bg-gray-50 dark:hover:bg-gray-800 flex items-center space-x-3 transition-colors"
                      >
                        <LifeBuoy className="w-4 h-4 text-gray-400 dark:text-gray-500" />
                        <span className="text-sm text-gray-700 dark:text-gray-300">{t('navbar.profile.helpSupport')}</span>
                      </button>
                    </>
                  )}
                </div>
                )}
                
                <div className={profileMenuItems?.length === 0 ? 'py-2' : 'border-t border-gray-100 dark:border-gray-800 py-2'}>
                  <button 
                    onClick={handleSignOutClick}
                    className="w-full px-4 py-2 text-left hover:bg-gray-50 dark:hover:bg-gray-800 flex items-center space-x-3 transition-colors"
                  >
                    <LogOut className="w-4 h-4 text-gray-400 dark:text-gray-500" />
                    <span className="text-sm text-gray-700 dark:text-gray-300">{t('navbar.profile.signOut')}</span>
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </nav>
  )
}